import type { Timestamp } from 'firebase/firestore';

export type HandbookPolicyStatus = 'draft' | 'published';

export type HandbookBodyFormat = 'html' | 'plain';

export type HandbookChangeRequestStatus =
  | 'draft'
  | 'open'
  | 'approved'
  | 'changes_requested'
  | 'rejected'
  | 'withdrawn';

/** One company handbook policy document in `handbookPolicies`. */
export interface HandbookPolicy {
  id: string;
  policyNumber: string;
  title: string;
  sortOrder: number;
  category?: string;
  currentVersion: string;
  effectiveDate: string;
  location: string;
  summary?: string;
  bodyFormat: HandbookBodyFormat;
  body: string;
  searchBlob?: string;
  tags?: string[];
  status: HandbookPolicyStatus;
  createdAt: Date;
  updatedAt: Date;
}

/** Immutable snapshot in `handbookPolicies/{id}/versions`. */
export interface HandbookPolicyVersion {
  id: string;
  policyId: string;
  version: string;
  title: string;
  effectiveDate: string;
  location: string;
  summary?: string;
  bodyFormat: HandbookBodyFormat;
  body: string;
  mergedFromChangeRequestId?: string;
  publishedByUid: string;
  publishedByName: string;
  publishedAt: Date;
}

/** Proposed edit in `handbookPolicies/{id}/changeRequests`. */
export interface HandbookChangeRequest {
  id: string;
  policyId: string;
  policyNumber: string;
  status: HandbookChangeRequestStatus;
  baseVersion: string;
  title: string;
  effectiveDate: string;
  location: string;
  summary?: string;
  bodyFormat: HandbookBodyFormat;
  body: string;
  authorUid: string;
  authorName: string;
  reviewNote?: string;
  reviewedByUid?: string;
  reviewedByName?: string;
  reviewedAt?: Date;
  mergedVersion?: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Firestore raw shape (Timestamps). */
export interface HandbookPolicyFirestore {
  policyNumber?: string;
  title?: string;
  sortOrder?: number;
  category?: string;
  currentVersion?: string;
  effectiveDate?: string;
  location?: string;
  summary?: string;
  bodyFormat?: HandbookBodyFormat;
  body?: string;
  searchBlob?: string;
  tags?: string[];
  status?: HandbookPolicyStatus;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export interface HandbookPolicyVersionFirestore {
  policyId?: string;
  version?: string;
  title?: string;
  effectiveDate?: string;
  location?: string;
  summary?: string;
  bodyFormat?: HandbookBodyFormat;
  body?: string;
  mergedFromChangeRequestId?: string;
  publishedByUid?: string;
  publishedByName?: string;
  publishedAt?: Timestamp | null;
}

export interface HandbookChangeRequestFirestore {
  policyId?: string;
  policyNumber?: string;
  status?: HandbookChangeRequestStatus;
  baseVersion?: string;
  title?: string;
  effectiveDate?: string;
  location?: string;
  summary?: string;
  bodyFormat?: HandbookBodyFormat;
  body?: string;
  authorUid?: string;
  authorName?: string;
  reviewNote?: string;
  reviewedByUid?: string;
  reviewedByName?: string;
  reviewedAt?: Timestamp | null;
  mergedVersion?: string;
  createdAt?: Timestamp | null;
  updatedAt?: Timestamp | null;
}

export const HANDBOOK_CR_STATUSES: readonly HandbookChangeRequestStatus[] = [
  'draft',
  'open',
  'approved',
  'changes_requested',
  'rejected',
  'withdrawn',
];

export const HANDBOOK_CR_STATUS_LABELS: Record<HandbookChangeRequestStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  approved: 'Merged',
  changes_requested: 'Changes requested',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};
